/**
 * Roadmap specific parsing logic
 */

import { readFile } from '../shared/fileReader';
import { isValidDateString } from '../shared/dateNormalizer';
import { VALID_ROADMAP_STATUS, VALID_ROADMAP_LEVELS } from '../../chartUtils';
import { 
  validateHeaders as validateRoadmapHeaders, 
  generateHeaderError as generateRoadmapHeaderError, 
  generateValidationError as generateRoadmapValidationError 
} from '../../validations/roadmap/validator';

/**
 * Processes hierarchy for roadmap items
 * @param {Array} processedData - Array of items to process hierarchy
 * @returns {Array} Items with hierarchy information added
 */
function processHierarchy(processedData) {
  let lastItemId = null;
  
  return processedData.map((item, index) => {
    const nivel = item.Nível?.toString().trim() || item['Nível']?.toString().trim();
    const normalizedLevel = nivel.toLowerCase();
    
    if (normalizedLevel === 'item') {
      item.level = 0;
      item.parentId = null;
      lastItemId = item.id;
    } else if (normalizedLevel === 'subitem') {
      item.level = 1;
      item.parentId = lastItemId;
    }
    
    return item;
  });
}

/**
 * Validates roadmap specific business rules
 * @param {Array} processedData - Array of items to validate
 * @returns {Array} Array of validation errors
 */
function validateRoadmapRules(processedData) {
  const validationErrors = [];
  let lastItemId = null;
  
  processedData.forEach((item, index) => {
    const lineNumber = index + 2;
    const status = item.Status?.toString().trim();
    const nome = item.Nome?.toString().trim();
    const nivel = item.Nível?.toString().trim() || item['Nível']?.toString().trim();
    
    // Validar se o nome está presente
    if (!nome) {
      validationErrors.push({
        type: 'missing_name',
        line: lineNumber,
        message: `Linha ${lineNumber}: O campo 'Nome' é obrigatório e não pode estar vazio.`
      });
    }
    
    // Validar nível
    if (!nivel) {
      validationErrors.push({
        type: 'missing_level',
        line: lineNumber,
        name: nome || 'Nome não informado',
        message: `Linha ${lineNumber}: O campo 'Nível' é obrigatório e não pode estar vazio (Nome: "${nome || 'Nome não informado'}").`
      });
    } else if (!VALID_ROADMAP_LEVELS.includes(nivel) && !VALID_ROADMAP_LEVELS.map(l => l.toLowerCase()).includes(nivel.toLowerCase())) {
      validationErrors.push({
        type: 'invalid_level',
        line: lineNumber,
        level: nivel,
        name: nome || 'Nome não informado',
        message: `Linha ${lineNumber}: Nível "${nivel}" inválido para "${nome || 'Nome não informado'}". Use apenas: ${VALID_ROADMAP_LEVELS.join(', ')}.`
      });
    } else {
      // Nível válido - verificar hierarquia
      const normalizedLevel = nivel.toLowerCase();
      if (normalizedLevel === 'item') {
        lastItemId = item.id;
      } else if (normalizedLevel === 'subitem') {
        if (!lastItemId) {
          validationErrors.push({
            type: 'orphan_subitem',
            line: lineNumber,
            name: nome || 'Nome não informado',
            message: `Linha ${lineNumber}: Subitem "${nome || 'Nome não informado'}" deve ter um item principal acima dele.`
          });
        }
      }
    }
    
    // Validar status
    if (!status) {
      validationErrors.push({
        type: 'missing_status',
        line: lineNumber,
        name: nome || 'Nome não informado',
        message: `Linha ${lineNumber}: O campo 'Status' é obrigatório e não pode estar vazio (Nome: "${nome || 'Nome não informado'}").`
      });
    } else if (!VALID_ROADMAP_STATUS.includes(status)) {
      validationErrors.push({
        type: 'invalid_status',
        line: lineNumber,
        status: status,
        name: nome || 'Nome não informado',
        message: `Linha ${lineNumber}: Status "${status}" inválido para "${nome || 'Nome não informado'}".`
      });
    } else if (status === 'Concluído') {
      // Validação adicional para status Concluído
      const dataInicio = item['Data de Inicio']?.toString().trim();
      const previsaoEntrega = item['Previsão de Entrega']?.toString().trim();
      const dataConclusao = item['Data de Conclusão']?.toString().trim();
      
      // Verifica se as datas obrigatórias estão presentes
      if (!dataInicio) {
        validationErrors.push({
          type: 'missing_data_inicio',
          line: lineNumber,
          name: nome || 'Nome não informado',
          message: `Linha ${lineNumber}: Data de Início é obrigatória para itens concluídos (Nome: "${nome || 'Nome não informado'}").`
        });
      }
      if (!previsaoEntrega) {
        validationErrors.push({
          type: 'missing_previsao_entrega',
          line: lineNumber,
          name: nome || 'Nome não informado',
          message: `Linha ${lineNumber}: Previsão de Entrega é obrigatória para itens concluídos (Nome: "${nome || 'Nome não informado'}").`
        });
      }
      if (!dataConclusao) {
        validationErrors.push({
          type: 'missing_data_conclusao',
          line: lineNumber,
          name: nome || 'Nome não informado',
          message: `Linha ${lineNumber}: Data de Conclusão é obrigatória para itens concluídos (Nome: "${nome || 'Nome não informado'}").`
        });
      }
      
      // Verifica se as datas têm formato válido (somente se estiverem presentes)
      if (dataInicio && !isValidDateString(dataInicio)) {
        validationErrors.push({
          type: 'invalid_data_inicio_format',
          line: lineNumber,
          name: nome || 'Nome não informado',
          message: `Linha ${lineNumber}: Data de Início com formato inválido "${dataInicio}" (Nome: "${nome || 'Nome não informado'}"). Use formato dd/mm/aaaa.`
        });
      }
      if (previsaoEntrega && !isValidDateString(previsaoEntrega)) {
        validationErrors.push({
          type: 'invalid_previsao_entrega_format',
          line: lineNumber,
          name: nome || 'Nome não informado',
          message: `Linha ${lineNumber}: Previsão de Entrega com formato inválido "${previsaoEntrega}" (Nome: "${nome || 'Nome não informado'}"). Use formato dd/mm/aaaa.`
        });
      }
      if (dataConclusao && !isValidDateString(dataConclusao)) {
        validationErrors.push({
          type: 'invalid_data_conclusao_format',
          line: lineNumber,
          name: nome || 'Nome não informado',
          message: `Linha ${lineNumber}: Data de Conclusão com formato inválido "${dataConclusao}" (Nome: "${nome || 'Nome não informado'}"). Use formato dd/mm/aaaa.`
        });
      }
      
      // Só compara datas se ambas existirem E forem válidas
      if (dataInicio && dataConclusao && isValidDateString(dataInicio) && isValidDateString(dataConclusao)) {
        const dInicio = new Date(dataInicio);
        const dConclusao = new Date(dataConclusao);
        if (dConclusao < dInicio) {
          validationErrors.push({
            type: 'invalid_data_conclusao',
            line: lineNumber,
            name: nome || 'Nome não informado',
            message: `Linha ${lineNumber}: Data de Conclusão deve ser maior ou igual à Data de Início (Nome: "${nome || 'Nome não informado'}").`
          });
        }
      }
    }
  });
  
  return validationErrors;
}

/**
 * Parses a roadmap file and returns validated roadmap data
 * @param {File} file - The roadmap file to process
 * @returns {Array} Processed roadmap data with validation and hierarchy
 */
export async function parseRoadmapFile(file) {
  // Read file data
  const { headers, rawData } = await readFile(file);
  
  // Validate headers
  const missingHeaders = validateRoadmapHeaders(headers);
  if (missingHeaders.length > 0) {
    throw new Error(generateRoadmapHeaderError(missingHeaders, headers));
  }

  // Add unique IDs to items
  const processedData = rawData.map((item, index) => ({
    ...item,
    id: Date.now() + index
  }));

  // Validate roadmap specific rules
  const validationErrors = validateRoadmapRules(processedData);
  if (validationErrors.length > 0) {
    throw new Error(generateRoadmapValidationError(validationErrors));
  }

  // Process hierarchy
  return processHierarchy(processedData);
}
